import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CatalogValueEntity } from './catalog-value.entity';
import { ClientEntity } from './client.entity';
import { ProjectEntity } from './project.entity';
import { ProjectParticipantEntity } from './project-participant.entity';
import { WholesalerEntity } from './wholesaler.entity';

/**
 * PD-17109 — a competitor (a `wholesaler` row) on a project: whom it is
 * supplying or quoting ("Ferguson is supplying Limbach"), the scope (product
 * groups, in words), where it stands (a tenant-editable status that maps to
 * a fixed outcome), its price only when a rep gave one, and the note, which
 * is the main payload: reps mostly give words, not numbers.
 *
 * Whom it supplies is either a customer on the project
 * (`fk_client__id_supplying`, whether that customer is on the project through
 * its bid or job or a participant row) or a typed company's participant row
 * (`fk_project_participant__id`); at most one of the two. The same
 * competitor may be on a project several times, once per company it
 * supplies, so there is no unique index; the service updates the matching
 * row instead of adding a second one.
 *
 * SM1 exposure: none (a new table).
 */
@Index('project_competitor__fk_project__id', ['fkProjectId'], {})
@Index('project_competitor__fk_wholesaler__id', ['fkWholesalerId'], {})
@Entity('project_competitor')
export class ProjectCompetitorEntity extends BaseEntity {
	@Column('int', { name: 'fk_project__id', unsigned: true })
	public fkProjectId!: number;

	@Column('int', { name: 'fk_wholesaler__id', unsigned: true })
	public fkWholesalerId!: number;

	/** The customer the competitor supplies or quotes on this project. */
	@Column('int', { name: 'fk_client__id_supplying', unsigned: true, nullable: true })
	public fkClientIdSupplying!: number | null;

	/** The typed (non-customer) company it supplies, by its participant row. */
	@Column('int', { name: 'fk_project_participant__id', unsigned: true, nullable: true })
	public fkProjectParticipantId!: number | null;

	/** What they supply or quote, in words ("PVC and cast iron", "all the fire"). */
	@Column('varchar', { name: 'scope', length: 255, nullable: true })
	public scope!: string | null;

	/** A Competitor Statuses catalog value. */
	@Column('int', { name: 'fk_catalog_value__id_status', unsigned: true, nullable: true })
	public statusId!: number | null;

	/** Their price, only when a rep gave one. */
	@Column('decimal', { name: 'theirPrice', precision: 15, scale: 2, nullable: true })
	public theirPrice!: string | null;

	@Column('varchar', { name: 'note', length: 2000, nullable: true })
	public note!: string | null;

	@ManyToOne(() => ProjectEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_project__id', referencedColumnName: 'id' }])
	public project!: ProjectEntity;

	@ManyToOne(() => WholesalerEntity, { onDelete: 'RESTRICT', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_wholesaler__id', referencedColumnName: 'id' }])
	public competitor!: WholesalerEntity;

	@ManyToOne(() => ClientEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id_supplying', referencedColumnName: 'id' }])
	public supplyingClient!: ClientEntity | null;

	@ManyToOne(() => ProjectParticipantEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_project_participant__id', referencedColumnName: 'id' }])
	public supplyingParticipant!: ProjectParticipantEntity | null;

	@ManyToOne(() => CatalogValueEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_catalog_value__id_status', referencedColumnName: 'id' }])
	public status!: CatalogValueEntity | null;
}
